import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ProductApiService } from '../../../services/product-api.service';
import { ProductDetails, VariantDetails } from '../../../../../shared/models/product-details';
import { ToastNotificationService } from '../../../../../notifications/services/toast-notification.service';
import { ToastNotificationType, ToastPosition } from '../../../../../notifications/models/toast-notification-type';
import { RatingCount } from '../../../../../shared/models/rating-count';
import { PaginatedProductReviews } from '../../../models/PaginatedProductReviews.model';
import { TablePaginationComponent } from "../../../../../shared/components/table-pagination/table-pagination.component";
import { LimitOffsetPagination } from '../../../../../shared/models/limit-offset-pagination';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../../../shared/services/translations/language.service';
import { ApiErrorService } from '../../../../../shared/services/api-error.service';

@Component({
  selector: 'app-product-details',
  imports: [CommonModule, RouterLink, TablePaginationComponent, TranslateModule],
  templateUrl: './product-details.component.html',
  styleUrl: './product-details.component.css'
})
export class ProductDetailsComponent implements OnInit {

  route = inject(ActivatedRoute);
  router = inject(Router);
  apiService = inject(ProductApiService);
  apiErrorService = inject(ApiErrorService);
  product: ProductDetails | undefined;
  translate = inject(TranslateService);
  languageService = inject(LanguageService);

  productId = Number(this.route.snapshot.paramMap.get('id'));
  selectedVariant: VariantDetails | undefined;
  selectedImage: any;

  toastNotificationService = inject(ToastNotificationService);
  reviewsSummary: RatingCount[] = [];

  reviews: PaginatedProductReviews | undefined;


  pagination: LimitOffsetPagination = { limit: 10, offset: 0 };



  ratings = [
    { star: 5, count: 298, color: 'bg-green-500' },
    { star: 4, count: 140, color: 'bg-purple-400' },
    { star: 3, count: 40, color: 'bg-yellow-400' },
    { star: 2, count: 40, color: 'bg-blue-400' },
    { star: 1, count: 2, color: 'bg-orange-400' },
  ];

  colorMap: Record<number, string> = {
    5: 'bg-green-500',
    4: 'bg-purple-400',
    3: 'bg-yellow-400',
    2: 'bg-blue-400',
    1: 'bg-orange-400',
  };

  totalReviews = 320;
  averageRating = 4.0;

  ngOnInit(): void {
    this.getProduct();
    this.getProductReviews();
    this.getProductReviewsData();
  }

  getProductReviews() {
    this.apiService.getProductReviews(this.productId).subscribe({
      next: (reviews) => {
        this.reviews = reviews;
      },
      error: (error) => {
      }
    });
  }

  getProductReviewsData() {
    this.apiService.getProductReviewsData(this.productId).subscribe({
      next: (data) => {
        this.reviewsSummary = data.sort((a, b) => b.rating - a.rating);
        this.ratings = this.reviewsSummary
          .filter(item => item.rating > 0)
          .map(item => ({
            star: item.rating,
            count: item.count,
            color: this.colorMap[item.rating] ?? 'bg-gray-300', // fallback color
          }));

      },
      error: (error) => {
      }
    });
  }

  getProduct() {
    if (this.productId) {
      this.apiService.getProductById(this.productId).subscribe({
        next: (product) => {
          this.product = product;
          this.selectedVariant = this.product.variants.find(v => v.is_default) ?? this.product.variants[0];
          this.selectedImage = this.selectedVariant?.images[0];
        },
        error: (error) => {
          this.toastNotificationService.createNotification(this.translate.instant('products.errors.load_failed_message'), ToastNotificationType.ERROR, this.translate.instant('products.errors.load_failed_title'), ToastPosition.TOP_RIGHT);
        }
      });
    }
  }

  selectVariant(variant: VariantDetails) {
    this.selectedVariant = variant;
    this.selectedImage = variant.images[0];
  }

  selectImage(image: any) {
    this.selectedImage = image;
  }


  deleteProduct() {
    this.apiService.deleteProductById(this.productId).subscribe({
      next: (response) => {
        this.toastNotificationService.createNotification(this.translate.instant('products.success.deleted_message'), ToastNotificationType.SUCCESS, this.translate.instant('products.success.deleted_title'), ToastPosition.TOP_RIGHT);
        this.router.navigate(['/products']);
      },
      error: (err) => {
        this.toastNotificationService.createNotification(this.apiErrorService.getDisplayMessage(err, this.translate.instant('products.errors.generic_failed')), ToastNotificationType.ERROR, this.translate.instant('products.errors.delete_failed_title'), ToastPosition.TOP_RIGHT);
      }
    });
  }

  getRatingsSummary(data: RatingCount[] = this.reviewsSummary) {
    const totalCount = data.reduce((sum, item) => sum + item.count, 0);

    const weightedSum = data.reduce(
      (sum, item) => sum + item.rating * item.count,
      0
    );

    const average = totalCount > 0 ? weightedSum / totalCount : 0;

    return {
      totalCount,
      average,
    };
  }

  updatePagination(pagination: LimitOffsetPagination) {
    this.pagination = pagination;
    this.getProductReviews();
  }

  getVariantNameById(id: number): string | number {
    if(!this.product)
      return id;

    const variant = this.product.variants.find(item => item.id == id);
    if (!variant)
      return id;

    return this.languageService.currentLang() === 'ar'
      ? variant.name_ar
      : (variant.name_en || variant.name_ar);
  }



}
